import { models } from "types/api/viewModels.ts";
import { UserRole } from "types/declarations/enums.ts";
import { formatPriceWithPrefix } from "utils/string.ts";
import ErrorBanner from "components/Common/ErrorBanner/ErrorBanner.tsx";
import WarningBox from "components/Common/WarningBox/WarningBox.tsx";
import { CartItem } from "features/MarketingMaterials/pages/Checkout/components/CartItem/CartItem.tsx";
import {
	CartItemList,
	CartTotal,
	EstimatedTaxes
} from "features/MarketingMaterials/pages/Checkout/ViewCartPage.styles.ts";
import { ESTIMATED_TAXES } from "../../marketingMaterialsConstants.ts";

interface CartItemDetailsProps {
	showBanner: boolean;
	showInfoBanner?: boolean;
	validation?: models["ShoppingCartValidation"] | null;
	cartItems: models["ShoppingCartItem"][];
	subTotal: number;
	userRole?: models["UserRole"];
	isReviewOrConfirmation: boolean;
}

interface ValidationBannerCopy {
	title: string;
	description: string;
	footer?: string;
}

const quantityLimitMessagePattern = /\(\d+\s*max\)\s*$/i;

const quantityExceededValidationBannerCopy: ValidationBannerCopy = {
	title: "We're sorry, some items are unavailable.",
	description: "The following items have quantities that exceed what can be ordered:",
	footer: "Remove the items or reduce their quantities if you wish to proceed with your order."
};

const validationBannerCopyByCode: Record<string, ValidationBannerCopy> = {
	OutOfBounds: {
		title: "We're sorry, this order will exceed your monthly allowance.",
		description: "Your marketing allowance has a remaining balance of:",
		footer: "Please remove items from your cart or wait to place this order next month."
	},
	InvalidIncrement: {
		title: "We found a quantity issue in your cart",
		description: "Please resolve the following before checking out:"
	},
	Required: {
		title: "We found missing information in your cart",
		description: "Please resolve the following before checking out:"
	},
	NotFound: {
		title: "We found an unavailable item in your cart",
		description: "Please resolve the following before checking out:"
	},
	Invalid: {
		title: "We found an invalid item in your cart",
		description: "Please resolve the following before checking out:"
	},
	Duplicate: {
		title: "We found duplicate items in your cart",
		description: "Please resolve the following before checking out:"
	}
};

const defaultValidationBannerCopy: ValidationBannerCopy = {
	title: "We found an issue in your cart",
	description: "Please resolve the following before checking out:"
};

export const CartItemDetails = ({
	showBanner,
	showInfoBanner = true,
	cartItems,
	subTotal,
	userRole,
	validation,
	isReviewOrConfirmation = false
}: CartItemDetailsProps) => {
	const getTotalLabel = () => {
		return userRole === UserRole.SALES ? "Total" : "Subtotal";
	};
	const cartItemCount = cartItems.reduce((total, item) => total + (item.quantityOrdered ?? 0), 0);

	const blockingValidationErrors = validation?.errors ?? [];
	const infoValidationErrors = validation?.info ?? [];

	const getValidationBannerCopy = () => {
		const [firstError] = blockingValidationErrors;
		if (firstError?.code === "OutOfBounds" && quantityLimitMessagePattern.test(firstError.message ?? "")) {
			return quantityExceededValidationBannerCopy;
		}

		return validationBannerCopyByCode[firstError?.code ?? ""] ?? defaultValidationBannerCopy;
	};

	const getValidationErrorBanner = () => {
		if (!blockingValidationErrors.length) {
			return null;
		}
		const { title, description, footer } = getValidationBannerCopy();

		return (
			<ErrorBanner
				errorMessaging={
					<div>
						<div>{title}</div>
						<div>{description}</div>
						<ul>
							{blockingValidationErrors.map((error, index) => (
								<li key={`${error.code ?? error.id ?? "validation-error"}-${index}`}>
									{error.message ?? error.code ?? "Validation error"}
								</li>
							))}
						</ul>
						{footer && <div>{footer}</div>}
					</div>
				}
			/>
		);
	};

	const getValidationInfoBanner = () => {
		if (!infoValidationErrors.length) {
			return null;
		}

		return (
			<WarningBox
				isInfo
				warning="Backorder notice"
				message={
					<ul>
						{infoValidationErrors.map((infoError, index) => (
							<li key={`${infoError.code ?? infoError.id ?? "validation-info"}-${index}`}>
								{infoError.message ?? infoError.code ?? "Validation note"}
							</li>
						))}
					</ul>
				}
			/>
		);
	};

	return (
		<div>
			{showBanner && (
				<>
					{getValidationErrorBanner()}
					{showInfoBanner && getValidationInfoBanner()}
				</>
			)}
			<CartItemList>
				{cartItems.map((cartItem, index) => (
					<CartItem
						item={cartItem}
						key={index}
						lineNumber={index + 1}
						isEdit={!isReviewOrConfirmation}
					/>
				))}
			</CartItemList>
			<CartTotal>
				<div>
					<h6>{getTotalLabel()}</h6>
					<h5>{formatPriceWithPrefix(subTotal, false, true)}</h5>
				</div>
				{isReviewOrConfirmation && userRole != UserRole.SALES && (
					<EstimatedTaxes>{ESTIMATED_TAXES}</EstimatedTaxes>
				)}
				{!isReviewOrConfirmation && <p>{cartItemCount} total items</p>}
			</CartTotal>
		</div>
	);
};
